import firebase from "firebase/compat/app"
import {
  LicenseModel,
  Scheduling,
  SchedulingSetting,
  SiteLicenseModel,
  SiteLicenseType,
  AccountLicense,
  LicenseRegistry
} from "./types"
import { FirestoreTimestampType } from "../../snapcourse/db/types"
import { db } from "../firebaseConfig"
import { getSetById } from "../sets"
import { getLicensesSetById } from "../licensesSets"
import computeStudentGrade from "../../mp-core/helpers/users/computeStudentGrade"

export class SiteLicense {
  private readonly COLLECTION_NAME = "siteLicenses"
  private siteLicense: SiteLicenseModel | null
  public saved: boolean

  constructor(siteLicenseData?: { type?: SiteLicenseType; groupId: string }) {
    this.create(siteLicenseData?.groupId, siteLicenseData?.type)
  }

  public setType(type: SiteLicenseType): void {
    this.siteLicense.type = type
    this.siteLicense.id = this.buildId(this.siteLicense.groupId, type)
    this.saved = false
  }

  public getSiteLicense(): SiteLicenseModel {
    return this.siteLicense
  }

  public enable(): void {
    this.siteLicense.enabled = true
    this.saved = false
  }

  public disable(): void {
    this.siteLicense.enabled = false
    this.saved = false
  }

  public setLicense(licenseData: LicenseModel): void {
    const existLicenseIndex = this.siteLicense.licenses.findIndex(
      license =>
        license.content === licenseData.content &&
        license.product === licenseData.product &&
        license.program === licenseData.program
    )

    if (existLicenseIndex !== -1) {
      this.siteLicense.licenses[existLicenseIndex] = licenseData
      this.saved = false

      return
    }

    this.saved = false
    this.siteLicense.licenses.push(licenseData)
  }

  public setSchedulingSetting(
    licenseRegistryId: string,
    schedulingSetting: SchedulingSetting
  ): void {
    this.siteLicense.licenses.map(license => {
      if (licenseRegistryId === license.licenseRegistryId) {
        license.scheduling.schedulingSetting = schedulingSetting
        this.saved = false
      }

      return license
    })
  }

  public setSchedulingDates(
    licenseRegistryId: string,
    startDate: FirestoreTimestampType | null,
    endDate: FirestoreTimestampType | null
  ) {
    this.siteLicense.licenses.map(license => {
      if (licenseRegistryId === license.licenseRegistryId) {
        license.scheduling.startDate = startDate
        license.scheduling.endDate = endDate
        this.saved = false
      }

      return license
    })
  }

  public setGrades(grades: number[] | "ALL") {
    this.siteLicense.grades = grades
    this.saved = false
  }

  public removeLicense(licenseRegistryId: string): void {
    this.siteLicense.licenses = this.siteLicense.licenses.filter(
      license => license.licenseRegistryId !== licenseRegistryId
    )

    this.saved = false
  }

  public async save() {
    // First, if we're enabling this license and it is either DISTRICT or GRADE,
    // we should check whether an opposite license type is already enabled for this group.
    if (
      this.siteLicense.enabled &&
      (this.siteLicense.type === "DISTRICT" ||
        this.siteLicense.type === "GRADE")
    ) {
      // Determine opposite type
      const oppositeType = (
        this.siteLicense.type === "DISTRICT" ? "GRADE" : "DISTRICT"
      ) as SiteLicenseType

      // Build the document ID for the opposite type
      const oppositeId = this.buildId(this.siteLicense.groupId, oppositeType)

      // Reference the document of the opposite type
      const oppositeDocRef = db.collection(this.COLLECTION_NAME).doc(oppositeId)
      const oppositeDocSnap = await oppositeDocRef.get()

      if (oppositeDocSnap.exists) {
        const oppositeData = oppositeDocSnap.data() as SiteLicenseModel

        // If the opposite license is enabled, disable it
        if (oppositeData.enabled) {
          console.log(
            `Disabling the ${oppositeType} license for groupId: ${this.siteLicense.groupId}, since we are enabling a ${this.siteLicense.type} license.`
          )
          await oppositeDocRef.update({
            enabled: false,
            updateAt: firebase.firestore.FieldValue.serverTimestamp()
          })
        }
      }
    }

    // Proceed with the normal save operation
    this.siteLicense.updateAt = firebase.firestore.Timestamp.now()

    if (!this.siteLicense.id) {
      throw new Error('Cannot save: "id" is not provided.')
    }

    await db
      .collection(this.COLLECTION_NAME)
      .doc(this.siteLicense.id)
      .set({
        ...this.siteLicense,
        updateAt: firebase.firestore.FieldValue.serverTimestamp(),
        createdAt: this.siteLicense.createdAt
          ? new firebase.firestore.Timestamp(
              this.siteLicense.createdAt.seconds,
              0
            )
          : firebase.firestore.FieldValue.serverTimestamp()
      })

    this.saved = true
    return true
  }

  /**
   * Loads a site license for the given groupId.
   *
   * @param groupId - The ID of the group (school or district)
   * @param type - Optional license type (SCHOOL, DISTRICT, GRADE).
   *               If provided, loads the specific license with ID = groupId_type.
   *               If null/undefined, searches for ANY enabled license with the given groupId.
   *
   * Behavior:
   * - With type: Looks for document with ID "{groupId}_{type}" (e.g., "abc123_DISTRICT")
   * - Without type (null): Searches for any enabled license where groupId matches
   * - This allows flexibility to find any type of license for a group
   */
  public async load(groupId: string, type?: SiteLicenseType) {
    this.siteLicense.groupId = groupId
    this.siteLicense.type = type
    this.siteLicense.id = type ? this.buildId(groupId, type) : ""

    if (this.siteLicense.id) {
      const snapshot = await db
        .collection(this.COLLECTION_NAME)
        .doc(this.siteLicense.id)
        .get()

      if (!snapshot.exists) {
        // console.log(
        //   `No site license found for id: ${this.siteLicense.id}. Creating new one...`
        // )
        this.create(groupId, type)
      } else {
        this.siteLicense = snapshot.data() as SiteLicenseModel
        this.saved = true
      }

      return this.siteLicense
    }

    // console.log(
    //   `No site license id provided. Searching for site license by groupId: ${groupId}`
    // )

    if (groupId) {
      const query = await db
        .collection(this.COLLECTION_NAME)
        .where("groupId", "==", groupId)
        .where("enabled", "==", true)
        .get()

      const currentLicenses = query.docs.map(doc =>
        doc.data()
      ) as SiteLicenseModel[]

      if (currentLicenses.length > 1) {
        console.warn(
          `More then one license exist for groupId: ${groupId}. Returning last modified license...`
        )
        const currentLicense = currentLicenses.reduce((prev, current) =>
          prev && prev.updateAt.seconds > current.updateAt.seconds
            ? prev
            : current
        )

        this.siteLicense = currentLicense
        this.saved = true

        return this.siteLicense
      }

      if (currentLicenses.length === 0) {
        // console.log(
        //   `No site license found for id: ${this.siteLicense.id}. Creating new one...`
        // )
        this.create(groupId, type)
      } else {
        this.siteLicense = currentLicenses[0]
        this.saved = true
      }
    } else {
      this.create(groupId, type)
    }

    return this.siteLicense
  }

  private async create(groupId: string, type?: SiteLicenseType) {
    // Default to DISTRICT type if no type is provided
    const licenseType = type || SiteLicenseType.DISTRICT

    this.siteLicense = {
      id: groupId ? this.buildId(groupId, licenseType) : "",
      type: licenseType,
      groupId: groupId,
      enabled: false,
      licenses: [],
      createdAt: null,
      updateAt: null
    }

    this.saved = false
  }

  public async getLegacySeatLicenses(user: any) {
    if (!this.siteLicense?.licenses) return []

    const legacyLicenses = []
    const nowSeconds = Math.floor(Date.now() / 1000) // current time in seconds

    if (this.siteLicense.type === "GRADE" && user.role === "Student") {
      let grade = computeStudentGrade(user)

      // force to 13 if graduated
      if (grade === "graduated") {
        grade = 13
      }

      if (
        grade &&
        this.siteLicense.grades !== "ALL" &&
        !this.siteLicense.grades.includes(grade as number)
      ) {
        return []
      }
    }

    // Fetch license registry docs in parallel - contains license metadata (type, product, content)
    const docs = await Promise.all(
      this.siteLicense.licenses.map(license =>
        db.collection("licenseRegistry").doc(license.licenseRegistryId).get()
      )
    )

    // Transform array to lookup map for O(1) access by license registry ID
    const docList = docs.reduce((acc, docItem) => {
      acc[docItem.id] = docItem
      return acc
    }, {} as firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>[])

    // Fetch practice test sets in parallel - contains display info (name, navCategory)
    const sets = await Promise.all(
      this.siteLicense.licenses.map(license =>
        getSetById(license.licenseRegistryId)
      )
    )

    // Transform array to lookup map for O(1) access by license registry ID
    const setsList = sets.reduce((acc, setDoc) => {
      if (setDoc) {
        acc[setDoc.data.id] = setDoc
      }
      return acc
    }, {} as { [key: string]: any })

    // Fetch license sets in parallel - alternative source for display info
    const licenses = await Promise.all(
      this.siteLicense.licenses.map(license =>
        getLicensesSetById(license.licenseRegistryId)
      )
    )

    // Transform array to lookup map for O(1) access by license registry ID
    const licenseSetsList = licenses.reduce((acc, licenseSetDoc) => {
      if (licenseSetDoc) {
        acc[licenseSetDoc.data.id] = licenseSetDoc
      }
      return acc
    }, {} as { [key: string]: any })

    for (const license of this.siteLicense.licenses) {
      const registryDoc = docList[license.licenseRegistryId]
      const registryData = registryDoc.data() as LicenseRegistry

      const schedulingSetting = license.scheduling.schedulingSetting
      const startSeconds = license.scheduling.startDate?.seconds
      const endSeconds = license.scheduling.endDate?.seconds

      let isExpired = false

      // Check each scheduling setting to set isExpired or skip
      switch (schedulingSetting) {
        case SchedulingSetting.CLOSED:
          // If license is CLOSED, it is always expired
          isExpired = true
          break

        case SchedulingSetting.OPEN_WITH_START_DATE:
          // If the current date is before the start date, skip entirely
          if (startSeconds && nowSeconds < startSeconds) {
            continue // do not include this license
          }
          break

        case SchedulingSetting.OPEN_WITH_END_DATE:
          // If the current date is after the end date, it's expired
          if (endSeconds && nowSeconds > endSeconds) {
            isExpired = true
          }
          break
      }

      let record
      const setDoc = setsList[license.licenseRegistryId]

      if (setDoc) {
        record = setDoc?.data
      } else {
        const licenseSetDoc = licenseSetsList[license.licenseRegistryId]
        record = licenseSetDoc?.data
      }

      // If no corresponding record, skip
      if (!record) continue

      let licenseType = "student"
      if (user.role != "Student") {
        licenseType = "educator"
      }

      if (
        license.product === "TruScore" ||
        registryData.type.includes(licenseType)
      ) {
        legacyLicenses.push({
          id: license.licenseRegistryId, // same as licenseRegistryId
          name: record.name, // from sets/licenseSets doc
          navCategory: record.navCategory, // from sets/licenseSets doc
          navName: record.navName, // from sets/licenseSets doc
          licenseSet: license.licenseRegistryId, // same as licenseRegistryId
          licenseType: licenseType,
          licensedUsers: {
            [user.id]: true
          },
          isExpired // boolean flag
        })
      }
    }

    return legacyLicenses
  }

  public async loadSeatLicenses() {
    if (!this.siteLicense?.groupId) return

    const snapshot = await db
      .collection("accountLicenses")
      .where("accountId", "==", this.siteLicense.groupId)
      .get()

    const accountLicenses = snapshot.docs
      .map(doc => doc.data() as AccountLicense)
      .filter(license => !license.deleted)

    console.log(
      `Found ${accountLicenses.length} account licenses for groupId: ${this.siteLicense.groupId}`
    )

    for (const acctLic of accountLicenses) {
      const registryDoc = await db
        .collection("licenseRegistry")
        .doc(acctLic.licenseSet)
        .get()
      if (!registryDoc.exists) {
        // no matching licenseRegistry doc
        continue
      }

      const registryData = registryDoc.data() as LicenseRegistry
      if (!registryData.licenseable) {
        // skip if license is not licenseable
        continue
      }

      const newLicense: LicenseModel = {
        licenseRegistryId: acctLic.licenseSet,
        program: registryData.program,
        product: registryData.product,
        content: registryData.content,
        scheduling: {
          schedulingSetting: SchedulingSetting.OPEN_WITH_END_DATE,
          startDate: null,
          endDate: new firebase.firestore.Timestamp(
            Math.floor(acctLic.endDate / 1000),
            0
          )
        }
      }

      this.setLicense(newLicense)
    }
  }

  private buildId(groupId: string, type: SiteLicenseType): string {
    return `${groupId}-${type.toLowerCase()}`
  }

  private validateSchedulingSetting(scheduling: Scheduling): void {
    if (scheduling.schedulingSetting === SchedulingSetting.CLOSED) {
      if (scheduling.endDate !== null || scheduling.startDate !== null) {
        throw new Error("Closed scheduling setting not available for any date.")
      }
    }

    if (scheduling.schedulingSetting === SchedulingSetting.OPEN_WITH_END_DATE) {
      if (scheduling.endDate === null) {
        throw new Error(
          "End date should be defined when using scheduling setting 'Open with End date'."
        )
      }
    }

    if (
      scheduling.schedulingSetting === SchedulingSetting.OPEN_WITH_START_DATE
    ) {
      if (scheduling.startDate === null) {
        throw new Error(
          "Start date should be defined when using scheduling setting 'Open with start date'."
        )
      }
    }

    if (scheduling.endDate !== null && scheduling.startDate !== null) {
      if (scheduling.endDate.nanoseconds < scheduling.startDate.nanoseconds) {
        throw new Error("End date can not be less then start date.")
      }
    }
  }
}

export default SiteLicense
